import { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import type CrmCoreUser from '@/types/serializers/CrmCoreUser'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { ReassignmentPreview } from './ReassignmentPreview'

interface TransferUserDialogProps {
  user: CrmCoreUser | null
  open: boolean
  onOpenChange: (open: boolean) => void
  teams: { id: number; name: string }[]
}

interface PreviewResult {
  success: boolean
  summary?: Record<string, number>
  target_user?: string
  errors?: string[]
}

export function TransferUserDialog({
  user,
  open,
  onOpenChange,
  teams,
}: TransferUserDialogProps) {
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTeamId('')
      setPreview(null)
      setLoading(false)
      setProcessing(false)
    }
  }, [open])

  // Fetch preview when team is selected
  useEffect(() => {
    if (!selectedTeamId || !user) {
      setPreview(null)
      return
    }

    setLoading(true)
    fetch(`/admin/users/${user.id}/transfer_preview?target_team_id=${selectedTeamId}`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    })
      .then((res) => res.json())
      .then((data: PreviewResult) => {
        setPreview(data)
        setLoading(false)
      })
      .catch(() => {
        setPreview({ success: false, errors: ['Failed to load preview'] })
        setLoading(false)
      })
  }, [selectedTeamId, user])

  const handleConfirm = () => {
    if (!user || !selectedTeamId) return
    setProcessing(true)
    router.patch(`/admin/users/${user.id}/transfer`, {
      target_team_id: parseInt(selectedTeamId, 10),
    }, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setProcessing(false),
    })
  }

  const availableTeams = teams.filter((t) => t.id !== user?.team_id)
  const isBlocked = !selectedTeamId || loading || (preview !== null && !preview.success) || processing

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Transfer {user?.name}</AlertDialogTitle>
          <AlertDialogDescription>
            Select a target team to transfer this user and reassign their records.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a team..." />
            </SelectTrigger>
            <SelectContent>
              {availableTeams.map((team) => (
                <SelectItem key={team.id} value={String(team.id)}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {!selectedTeamId && (
            <p className="text-sm text-muted-foreground">
              Select a team to see reassignment preview.
            </p>
          )}

          {selectedTeamId && (
            <ReassignmentPreview
              summary={preview?.summary ?? {}}
              targetUser={preview?.target_user ?? ''}
              loading={loading}
              error={preview && !preview.success ? preview.errors?.join(', ') : undefined}
            />
          )}
        </div>

        <Separator />

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isBlocked}
          >
            {processing ? 'Transferring...' : 'Transfer'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
